import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import { Dialog, DialogTitle, Grid2 as Grid } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import React, { useEffect } from "react";

const useStyles = makeStyles((theme) => ({
	icon: {
		fontSize: 25,
		verticalAlign: "middle",
	},
	success: {
		color: theme.palette.custom.icon.darkGreen,
	},
	error: {
		color: theme.palette.custom.icon.darkRed,
	},
	info: {
		color: theme.palette.secondary.main,
	},
	warning: {
		color: theme.palette.custom.icon.orange,
	},
	body2: {
		fontWeight: "normal",
	},
}));

export type AlertModalProps = {
	isOpen: boolean;
	title: string;
	status: "success" | "info" | "warning" | "error";
	onClose?: () => void;
};

const AlertModal: React.FC<AlertModalProps> = ({ isOpen, title, status, onClose }) => {
	const classes = useStyles();
	const theme = useTheme();

	useEffect(() => {
		const timer = setTimeout(() => {
			onClose && onClose();
		}, 2000);
		return () => clearTimeout(timer);
	}, [isOpen, onClose]);

	const getIcon = () => {
		switch (status) {
			case "success":
				return (
					<Grid container justifyContent="center" alignItems="center" direction="row" spacing={2}>
						<Grid>
							<CheckCircleIcon className={`${classes.icon} ${classes.success}`} />
						</Grid>
						<Grid>
							<div style={{ color: theme.palette.custom.icon.darkGreen }}>{title}</div>
						</Grid>
					</Grid>
				);
			case "warning":
				return (
					<Grid container justifyContent="center" alignItems="center" direction="row" spacing={2}>
						<Grid>
							<WarningIcon className={`${classes.icon} ${classes.warning}`} />
						</Grid>
						<div style={{ color: theme.palette.custom.icon.orange }}>{title}</div>
					</Grid>
				);
			case "info":
				return (
					<Grid container justifyContent="center" alignItems="center" direction="row" spacing={2}>
						<Grid>
							<ErrorIcon className={`${classes.icon} ${classes.error}`} />
						</Grid>
						<Grid>
							<div style={{ color: theme.palette.custom.icon.main }}>{title}</div>
						</Grid>
					</Grid>
				);
			default:
				return (
					<Grid container justifyContent="center" alignItems="center" direction="row" spacing={2}>
						<Grid>
							<ErrorIcon className={`${classes.icon} ${classes.error}`} />
						</Grid>
						<div style={{ color: theme.palette.custom.icon.darkRed }}>{title}</div>
					</Grid>
				);
		}
	};

	const getDialogStyle = (): React.CSSProperties => {
		const borderColor =
			status === "success"
				? theme.palette.custom.icon.darkGreen
				: status === "warning"
				? theme.palette.custom.icon.orange
				: status === "info"
				? theme.palette.secondary.main
				: theme.palette.custom.icon.darkRed;

		return {
			border: `2px solid ${borderColor}`,
			height: "100px",
			display: "flex",
			flexDirection: "column",
			justifyContent: "center",
		};
	};

	return (
		<Dialog
			open={isOpen}
			PaperProps={{
				style: getDialogStyle(),
			}}
		>
			<DialogTitle style={theme.typography.h6 as React.CSSProperties}>{getIcon()}</DialogTitle>
		</Dialog>
	);
};

export default AlertModal;
